// Central de notificações — página cheia (o sino no header, NotificationBell.jsx,
// mostra só as ~15 mais recentes num dropdown; aqui dá pra ver as últimas 50 e,
// se for master, escrever um aviso de sistema novo pra todo mundo ou pra uma
// instituição específica).
import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Megaphone,
  UserPlus,
  ArrowRightLeft,
  UserMinus,
  Plus,
  CheckCheck,
  AlertTriangle,
  Bell,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useInstitution } from '../../contexts/InstitutionContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Button, Input, Select, Modal } from '../../Components/ui';
import { useToast } from '../../Components/Feedback';
import NotificacaoDetalheModal from '../../Components/NotificacaoDetalheModal';
import { API_URL } from '../../api/config';
import { useApi } from '../../api/useApi';
import { subscreverPush } from '../../utils/pushNotification';

const ICONE_TIPO = {
  sistema: {
    Icon: Megaphone,
    cor: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30',
    label: 'Sistema',
  },
  matricula: {
    Icon: UserPlus,
    cor: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30',
    label: 'Matrícula',
  },
  movimentacao: {
    Icon: ArrowRightLeft,
    cor: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30',
    label: 'Mudança de turma',
  },
  desistencia: {
    Icon: UserMinus,
    cor: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30',
    label: 'Desistência',
  },
  chamada_pendente: {
    Icon: AlertTriangle,
    cor: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30',
    label: 'Chamada Pendente',
  },
};

// timeZone explícito: sem isso, a hora exibida seguiria o fuso configurado no
// SISTEMA do navegador (não o relógio, o fuso), que pode estar errado mesmo
// com a hora "certa" na tela — mesmo cuidado de agoraBrasil() no backend.
const formatarData = (dataIso) =>
  new Date(dataIso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });

function Notificacoes() {
  const { institutionId, institutionName } = useInstitution();
  const { token, user } = useAuth();
  const { podeCriar } = usePermissao('/notificacoes');
  const podeEnviarAviso = user?.perfil === 'master' || user?.perfil === 'coordenador' || podeCriar;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [detalheAberto, setDetalheAberto] = useState(null);

  const authHeaders = { 'x-institution-id': institutionId, Authorization: `Bearer ${token}` };
  const api = useApi();

  const { data: notificacoes = [], isLoading } = useQuery({
    queryKey: ['notificacoes-recentes', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/notificacoes`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
  });

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['notificacoes-recentes', institutionId] });
    queryClient.invalidateQueries({ queryKey: ['notificacoes-contagem', institutionId] });
  };

  const marcarLida = async (id) => {
    await fetch(`${API_URL}/api/notificacoes/${id}/marcar-lida`, {
      method: 'POST',
      headers: authHeaders,
    });
    invalidar();
  };

  const marcarTodasLidas = async () => {
    await fetch(`${API_URL}/api/notificacoes/marcar-todas-lidas`, {
      method: 'POST',
      headers: authHeaders,
    });
    invalidar();
  };

  // Clicar numa notificação de evento abre o detalhe da alteração (o "de/para"
  // estruturado — ver NotificacaoDetalheModal), mesmo comportamento do sino
  // (NotificationBell.jsx). Aviso de sistema não representa uma alteração de
  // dado, só marca como lido.
  const temDetalhe = (n) => n.tipo !== 'sistema';

  const irParaNotificacao = (n) => {
    if (!n.lida) marcarLida(n.id);
    if (temDetalhe(n)) setDetalheAberto(n);
  };

  // --- Form de aviso de sistema (só master) ---
  const [modalAberto, setModalAberto] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [instituicaoAlvo, setInstituicaoAlvo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const { data: instituicoes = [] } = useQuery({
    queryKey: ['instituicoes-todas'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/instituicoes/todas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erro ao carregar instituições');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: modalAberto && user?.perfil === 'master',
  });

  const abrirNovoAviso = () => {
    setTitulo('');
    setMensagem('');
    setInstituicaoAlvo(user?.perfil === 'master' ? '' : String(institutionId || ''));
    setModalAberto(true);
  };

  const enviarAviso = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const idInstituicaoPayload =
        user?.perfil === 'master'
          ? (instituicaoAlvo ? Number(instituicaoAlvo) : null)
          : (institutionId ? Number(institutionId) : null);

      const res = await fetch(`${API_URL}/api/notificacoes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          titulo,
          mensagem,
          id_instituicao: idInstituicaoPayload,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao criar aviso');
      toast.success(data?.message || 'Aviso publicado.');
      setModalAberto(false);
      invalidar();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEnviando(false);
    }
  };

  const [ativandoPush, setAtivandoPush] = useState(false);
  const [pushCadastrado, setPushCadastrado] = useState(false);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      Notification.permission === 'granted'
    ) {
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => {
          if (sub) setPushCadastrado(true);
        })
        .catch(() => {});
    }
  }, []);

  const handleAtivarPush = async () => {
    try {
      setAtivandoPush(true);
      await subscreverPush(token, institutionId);
      setPushCadastrado(true);
      toast.success('Notificações ativadas com sucesso neste aparelho!');
    } catch (err) {
      toast.error(err.message || 'Erro ao ativar notificações.');
    } finally {
      setAtivandoPush(false);
    }
  };

  const naoLidas = notificacoes.filter((n) => !n.lida).length;

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-2xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {naoLidas > 0 ? `${naoLidas} não lida${naoLidas === 1 ? '' : 's'}` : 'Tudo em dia'}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              icon={Bell}
              onClick={handleAtivarPush}
              loading={ativandoPush}
              disabled={pushCadastrado}
            >
              {pushCadastrado ? 'Notificações Ativas ✓' : 'Ativar no aparelho'}
            </Button>
            {naoLidas > 0 && (
              <Button variant="secondary" icon={CheckCheck} onClick={marcarTodasLidas}>
                Marcar todas como lidas
              </Button>
            )}
            {podeEnviarAviso && (
              <Button variant="primary" icon={Plus} onClick={abrirNovoAviso}>
                Novo aviso
              </Button>
            )}
          </div>
        </div>

        <Card padding="none" className="overflow-hidden">
          {isLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-12">
              Carregando...
            </p>
          ) : notificacoes.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-12">
              Nenhuma notificação ainda.
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {notificacoes.map((n) => {
                const { Icon, cor, label } = ICONE_TIPO[n.tipo] || ICONE_TIPO.sistema;
                const clicavel = temDetalhe(n);
                return (
                  <div
                    key={n.id}
                    onClick={() => irParaNotificacao(n)}
                    title={clicavel ? 'Ver detalhes da alteração' : undefined}
                    className={`flex items-start gap-3 px-4 py-3 transition-colors ${!n.lida ? 'bg-blue-50/40 dark:bg-blue-900/10 hover:bg-blue-50/70 dark:hover:bg-blue-900/20' : ''} ${clicavel ? 'cursor-pointer' : ''}`}
                  >
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cor}`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p
                          className="text-sm font-medium text-gray-900 dark:text-gray-100"
                          translate="no"
                        >
                          {n.titulo}
                        </p>
                        <span className="text-[10px] text-gray-400 dark:text-gray-600 uppercase tracking-wide flex-shrink-0">
                          {label}
                        </span>
                      </div>
                      {n.mensagem && (
                        <p
                          className="text-xs text-gray-500 dark:text-gray-400 mt-0.5"
                          translate="no"
                        >
                          {n.mensagem}
                        </p>
                      )}
                      <p className="text-[10px] text-gray-400 dark:text-gray-600 mt-1">
                        {formatarData(n.created_at)}
                      </p>
                    </div>
                    {!n.lida && (
                      <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </main>

      <Modal
        isOpen={modalAberto}
        onClose={() => setModalAberto(false)}
        title={user?.perfil === 'master' ? 'Novo aviso de sistema' : 'Nova notificação pontual'}
        maxWidth="md"
      >
        <form onSubmit={enviarAviso} className="space-y-4">
          <Input
            label="Título"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            required
            autoFocus
          />
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Mensagem
            </label>
            <textarea
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
            />
          </div>
          {user?.perfil === 'master' ? (
            <Select
              label="Enviar para"
              value={instituicaoAlvo}
              onChange={(e) => setInstituicaoAlvo(e.target.value)}
              options={[
                { value: '', label: 'Todas as instituições' },
                ...instituicoes.map((i) => ({ value: i.id, label: i.nome })),
              ]}
            />
          ) : (
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                Enviar para
              </label>
              <div className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300 font-medium">
                {institutionName || 'Instituição atual'}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalAberto(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={enviando}>
              {enviando ? 'Publicando...' : 'Publicar'}
            </Button>
          </div>
        </form>
      </Modal>

      <NotificacaoDetalheModal notificacao={detalheAberto} onClose={() => setDetalheAberto(null)} />
    </div>
  );
}

export default Notificacoes;
